import Loading from '@merq/web-ui/dist/components/Loading';
import { useAuth } from '@merq/web-ui/dist/providers/userProvider';
import * as Sentry from '@sentry/react';
import AuthRoute from 'components/AnimatedRouter/AuthRoute';
import { AnimatedRoutes, RouteTransition } from 'components/AnimatedRouter/RouteTransition';
import Layout from 'components/Layout';
import LoaderProvider from 'components/Layout/LoadingPage';
import VersionChecker from 'components/VersionChecker';
import { useOauthPermissions } from 'hooks/usePermissions';
import useTaskosaur from 'hooks/useTaskosaur';
import React, { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, BrowserRouter as Router, useLocation, useParams } from 'react-router-dom';
import { pageAction, pageName } from 'store/Permission/store';
import { adminRoutes, authRoutes, routes } from './routes';
import './settings/locale';
import './styles/global.scss';

const LoginToContinue = lazy(() => import('./pages/LoginToContinue'));

const AppContent = () => {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();

  useTaskosaur();

  useEffect(() => {
    // fix [PES-633]: when the user is on a french page, and leaves by closing the tab,
    // the next time they open the website, it will be in french,
    // this restores the language to english if they are not on an index page.
    const isIndexPage = location.pathname.match('/indices/.+');

    if (!isIndexPage && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [location.pathname]);

  const email = auth?.user?.email ?? '';
  const fullName =
    auth?.user?.displayName ??
    `${auth?.user?.firstName ?? ''} ${auth?.user?.lastName ?? ''}`.trim();

  Sentry.setUser({
    fullName,
    email,
  });

  return (
    <LoaderProvider>
      <Layout>
        <VersionChecker />
        <Suspense fallback={<Loading active={true} height={500} />}>
          <AnimatedRoutes>
            {Object.values(authRoutes).map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <RouteTransition>
                    <AuthRoute auth={auth} authLevel={route.level}>
                      <route.component />
                    </AuthRoute>
                  </RouteTransition>
                }
              />
            ))}
            {Object.values(adminRoutes).map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <RouteTransition>
                    <AdminOrNotFound namespace={route.namespace} level={route.level}>
                      <route.component />
                    </AdminOrNotFound>
                  </RouteTransition>
                }
              />
            ))}
            {Object.values(routes).map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <RouteTransition>
                    <route.component />
                  </RouteTransition>
                }
              />
            ))}
            <Route
              path="/account/my-indices"
              element={<Navigate to="/garage/my-indices" replace />}
            />
            <Route path="/account/new-index/:indexType" element={<RedirectToIndexCreate />} />
          </AnimatedRoutes>
        </Suspense>
      </Layout>
    </LoaderProvider>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;

const RedirectToIndexCreate = () => {
  const { indexType } = useParams();
  return <Navigate to={`/garage/new-index/${indexType}`} replace />;
};

const AdminOrNotFound: React.FC<
  React.PropsWithChildren<{
    namespace: pageName;
    level?: pageAction;
  }>
> = ({ children, namespace, level = 'VIEW' }) => {
  const { user } = useAuth();
  const permissions = useOauthPermissions().permissions.website;

  const authorized =
    permissions &&
    namespace &&
    permissions[namespace] &&
    (permissions[namespace].actions.includes(level) ||
      permissions[namespace].actions.includes('ADMIN'));

  if (!user) {
    return <LoginToContinue />;
  }

  return authorized ? <>{children}</> : null;
};
